/**
 * Jewelpa Custom Header, Mega Menu & Native <dialog> Mobile Drawer Interactions
 * Vanilla ES Module, zero external dependencies, accessible keyboard navigation
 */

(function () {
  'use strict';

  function initHeader() {
    const headerWrapper = document.querySelector('[data-custom-header]');
    if (!headerWrapper) return;

    /* ------------------------------------------------------------------------
       1. DESKTOP MEGA MENU HOVER-INTENT & KEYBOARD NAVIGATION
       ------------------------------------------------------------------------ */
    const navItems = headerWrapper.querySelectorAll('.custom-header__nav-item[data-has-megamenu]');
    const HOVER_DELAY = 130;

    navItems.forEach((item) => {
      let enterTimer = null;
      let leaveTimer = null;
      const triggerLink = item.querySelector('.custom-header__nav-link');

      let iconsLoaded = false;
      function loadIcons() {
        if (iconsLoaded) return;
        iconsLoaded = true;
        const iconImages = item.querySelectorAll('img[data-src]');
        iconImages.forEach((img) => {
          const src = img.getAttribute('data-src');
          if (src) {
            img.src = src;
            img.removeAttribute('data-src');
          }
        });
      }

      function openPanel() {
        clearTimeout(leaveTimer);
        loadIcons();
        navItems.forEach((other) => {
          if (other !== item) {
            other.classList.remove('is-open', 'is-active');
            const otherLink = other.querySelector('.custom-header__nav-link');
            if (otherLink) otherLink.setAttribute('aria-expanded', 'false');
          }
        });
        item.classList.add('is-open', 'is-active');
        if (triggerLink) triggerLink.setAttribute('aria-expanded', 'true');
      }

      function closePanel() {
        clearTimeout(enterTimer);
        item.classList.remove('is-open', 'is-active');
        if (triggerLink) triggerLink.setAttribute('aria-expanded', 'false');
      }

      // Pointer enter with hover-intent delay
      item.addEventListener('pointerenter', () => {
        clearTimeout(leaveTimer);
        loadIcons();
        enterTimer = setTimeout(openPanel, HOVER_DELAY);
      });

      // Pointer leave with grace period
      item.addEventListener('pointerleave', () => {
        clearTimeout(enterTimer);
        leaveTimer = setTimeout(closePanel, HOVER_DELAY);
      });

      // Focus management
      item.addEventListener('focusin', () => {
        clearTimeout(leaveTimer);
        loadIcons();
        openPanel();
      });

      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) {
          closePanel();
        }
      });

      // Escape key closes mega menu
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && item.classList.contains('is-open')) {
          closePanel();
          if (triggerLink) triggerLink.focus();
        }
      });
    });

    /* ------------------------------------------------------------------------
       2. NATIVE <dialog> MOBILE DRAWER NAVIGATION
       ------------------------------------------------------------------------ */
    const drawerTrigger = document.querySelector('[data-mobile-menu-trigger]');
    const drawer = document.querySelector('[data-custom-mobile-drawer]');
    if (!drawerTrigger || !drawer) return;

    const drawerPanel = drawer.querySelector('[data-mobile-drawer-panel]');
    const closeBtn = drawer.querySelector('[data-mobile-drawer-close]');
    let isClosing = false;

    function openMobileDrawer() {
      const defaultMenu = drawer.dataset.defaultOpenMenu;
      if (defaultMenu) {
        const item = [...drawer.querySelectorAll('[data-menu-key]')].find((entry) => entry.dataset.menuKey === defaultMenu);
        const toggle = item?.querySelector('[data-accordion-toggle]');
        if (toggle) {
          item.classList.add('is-expanded');
          toggle.setAttribute('aria-expanded', 'true');
          loadMobileDrawerIcons(item);
        }
      }

      if (typeof drawer.showModal === 'function') {
        drawer.showModal();
      } else {
        drawer.setAttribute('open', '');
      }

      drawerTrigger.classList.add('is-active');
      drawerTrigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';

      // Trigger entrance animation on next frame
      requestAnimationFrame(() => {
        drawer.classList.add('is-visible');
        const firstInput = drawer.querySelector('input, button');
        if (firstInput) firstInput.focus();
      });
    }

    function closeMobileDrawer() {
      if (isClosing) return;
      isClosing = true;

      drawer.classList.remove('is-visible');
      drawerTrigger.classList.remove('is-active');
      drawerTrigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';

      setTimeout(() => {
        if (typeof drawer.close === 'function') {
          drawer.close();
        } else {
          drawer.removeAttribute('open');
        }
        isClosing = false;
        drawerTrigger.focus();

        // Clean up expanded accordion states so re-opening starts fresh
        drawer.querySelectorAll('.custom-mobile-drawer__nav-item.is-expanded').forEach((item) => {
          item.classList.remove('is-expanded');
          const toggle = item.querySelector('[data-accordion-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        });
      }, 260);
    }

    drawerTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      if (drawer.open) {
        closeMobileDrawer();
      } else {
        openMobileDrawer();
      }
    });

    if (closeBtn) closeBtn.addEventListener('click', closeMobileDrawer);

    // Native dialog Escape handler
    drawer.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeMobileDrawer();
    });

    // Close when clicking outside drawer panel (on native ::backdrop / dialog surface)
    drawer.addEventListener('click', (event) => {
      if (drawerPanel && !drawerPanel.contains(event.target)) {
        closeMobileDrawer();
      }
    });

    // Lazy load collection shape icons when parent Level 1 section expands
    function loadMobileDrawerIcons(container) {
      const lazyImages = container.querySelectorAll('img[data-src]');
      lazyImages.forEach((img) => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
      });
    }

    // Level 1 accordion toggles (expand/collapse in place, auto-expand all level-2 groups)
    const level1Toggles = drawer.querySelectorAll('[data-accordion-toggle]');
    level1Toggles.forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const parentItem = toggle.closest('.custom-mobile-drawer__nav-item');
        if (!parentItem) return;

        const isExpanded = parentItem.classList.contains('is-expanded');
        if (isExpanded) {
          parentItem.classList.remove('is-expanded');
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          parentItem.classList.add('is-expanded');
          toggle.setAttribute('aria-expanded', 'true');
          loadMobileDrawerIcons(parentItem);
        }
      });
    });

    // Test parameter support for automated visual inspection
    if (window.location.search.includes('test_drawer=open')) {
      if (typeof drawer.showModal === 'function') drawer.showModal();
      else drawer.setAttribute('open', '');
      drawer.classList.add('is-visible');
    } else if (window.location.search.includes('test_drawer=engagement')) {
      if (typeof drawer.showModal === 'function') drawer.showModal();
      else drawer.setAttribute('open', '');
      drawer.classList.add('is-visible');
      const engToggle = drawer.querySelector('[data-accordion-toggle]');
      if (engToggle) {
        const parentItem = engToggle.closest('.custom-mobile-drawer__nav-item');
        if (parentItem) {
          parentItem.classList.add('is-expanded');
          engToggle.setAttribute('aria-expanded', 'true');
          loadMobileDrawerIcons(parentItem);
        }
      }
    }

    /* ------------------------------------------------------------------------
       3. LIVE CART COUNT SYNCHRONIZATION
       ------------------------------------------------------------------------ */
    function updateCartCounts(count) {
      const countDisplays = document.querySelectorAll('[data-header-cart-count]');
      countDisplays.forEach((el) => {
        el.textContent = count;
      });
    }

    // Listen for Shopify standard events
    document.addEventListener('cart:updated', (event) => {
      if (event.detail && typeof event.detail.item_count !== 'undefined') {
        updateCartCounts(event.detail.item_count);
      }
    });

    const originalFetch = window.fetch;
    if (originalFetch) {
      window.fetch = async function (...args) {
        const response = await originalFetch.apply(this, args);
        try {
          const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
          if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/clear') || url.includes('/cart/update')) {
            const clone = response.clone();
            clone.json().then((data) => {
              if (data && typeof data.item_count !== 'undefined') {
                updateCartCounts(data.item_count);
              }
            }).catch(() => {});
          }
        } catch (_) {}
        return response;
      };
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeader);
  } else {
    initHeader();
  }
})();
